import { Box, Button, Heading, Text } from '@chakra-ui/react'
import { FiDownload } from 'react-icons/fi'
import { ResponsiveTable } from '../../shared/ResponsiveTable'
import type { ReportRow, TableBlock } from '../../../types/reporting'
import { formatReportValue } from './reportFormat'

interface Props {
  block: TableBlock
  onExportCsv?: (blockId: string) => void
}

/**
 * Report rows have no id of their own. `ResponsiveTable.keyFn` is `(row: T) => string` -- row
 * only, no index -- so uniqueness has to be baked into the row shape before it reaches the
 * table. Pre-indexing here (rather than keying off column values) guarantees a unique, stable
 * key even when two data rows are identical, which a content-derived key cannot.
 */
interface IndexedRow {
  index: number
  row: ReportRow
}

/**
 * [L11-3e] The disclosure reports now read up to 25,000 rows (ValidationLimits
 * .MaxDisclosureReportRows), and this table has no virtualization — rendering 20,000 rows hangs
 * the browser. The viewer is a viewer; the complete record is the CSV, which is rendered
 * server-side from the frozen payload and is unaffected by this cap.
 */
const MAX_DISPLAYED_ROWS = 500

export function TableBlockView({ block, onExportCsv }: Props) {
  const totalRows = block.rows.length
  const isTruncated = totalRows > MAX_DISPLAYED_ROWS
  const indexedRows: IndexedRow[] = block.rows
    .slice(0, MAX_DISPLAYED_ROWS)
    .map((row, index) => ({ index, row }))

  const columns = block.columns.map((c, colIndex) => ({
    header: c.label,
    // isNumeric carried explicitly from block metadata -- dropping it right-aligns everything,
    // which is exactly what the A1.5 codemod did app-wide.
    isNumeric: c.isNumeric,
    mobileRole: colIndex === 0 ? ('title' as const) : ('meta' as const),
    cell: (indexedRow: IndexedRow) => {
      const raw = indexedRow.row[c.key]
      if (raw === null || raw === undefined) return '—'
      return typeof raw === 'number' ? formatReportValue(raw, c.format) : String(raw)
    },
  }))

  return (
    <Box className="report-block">
      {!!block.title && <Heading size="sm" mb={2}>{block.title}</Heading>}
      {!!onExportCsv && (
        <Button
          className="no-print"
          size="xs" variant="outline" mb={2}
          onClick={() => onExportCsv(block.id)}
        >
          <FiDownload /> CSV
        </Button>
      )}
      <ResponsiveTable
        columns={columns}
        rows={indexedRows}
        keyFn={(indexedRow) => String(indexedRow.index)}
        emptyMessage="No rows in this section."
      />
      {isTruncated && (
        <Text mt={2} fontSize="sm" color="muted">
          {`Showing the first ${MAX_DISPLAYED_ROWS} of ${totalRows} rows — `}
          {onExportCsv
            ? 'export CSV for the complete record.'
            : 'generate the run to export the complete record.'}
        </Text>
      )}
    </Box>
  )
}
